import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
import {
  ContactReportSectionBanner,
  DebugMenu,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  useReport,
  useReportMonitors,
  useReportRedirect,
  useSession,
  EmailReport as EmailReportType,
  EmailFraudCheckSection,
  useDarkWebReport,
  SerializedDarkWebBreach,
} from '@ltvco/refresh-lib/v1';
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  AssetsSection as AssetsSectionV2,
  PotentialOwners as PotentialOwnersV2,
  EmailReportOverview,
  EmailDarkWebResults,
  EmailDarkWebNoBreaches,
  EmailDarkWebLoadingScreen,
  EmailDarkWebVerify,
  ReportNullState,
  ReportClaimingSection,
} from '@ltvco/refresh-lib/v2';
import {} from 'navLinkData/phoneNavLinkData';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { constants } from 'appConstants';
import { ReportProps } from 'components/ChatBV/types/reportTypes';
import { getEmulatedUseQueryResult } from 'components/ChatBV/utils';

import imgIdCard from '../../../../../images/img_idCard.svg';

export function EmailChatBvReport({ permalink, reportData }: ReportProps) {
  const reportType = 'email';
  const { currentMonitors, reportIsMonitored } = useReportMonitors(permalink);
  const emulatedQueryResult = reportData
    ? getEmulatedUseQueryResult(reportData, true, false, false)
    : null;
  const coreResult =
    emulatedQueryResult || useReport(permalink, reportIsMonitored);

  const {
    session: { account },
  } = useSession();

  const { redirectToSearchContactUrl } = useReportRedirect();
  const { trackEvent } = useContext(AppConfig);
  const currentUserInfo = account?.account?.user_info;
  const queryResult = coreResult;

  const zeroed = isZeroed(queryResult as any);
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult as any);

  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [isDarkWebSectionLoading, setIsDarkWebSectionLoading] = useState(false);
  const [showDarkWebEmailSection, setShowDarkWebEmailSection] = useState(false);
  const [reportUpdateDate, setUpdateDate] = useState<string | undefined>();
  const [report, setReport] = useState<EmailReportType | null>(null);
  const date = new DateUtil();
  const { data: darkWebResults } = useDarkWebReport({
    email: report?.emailAddress as string,
  });

  const darkWebBreaches = darkWebResults?.data?.breaches ?? [];
  const breachUpdateDate = darkWebResults?.data?.rawData?.meta?.updated_at;
  const isInEmailDarkWebVariation = useFeatureIsOn('RFRSH-2939');

  const revealDarkWebEmailSection =
    isInEmailDarkWebVariation &&
    showDarkWebEmailSection &&
    !isDarkWebSectionLoading;

  const delayDarkWebLoading = 1000;
  function handleDarkWebSectionLoadingScreen() {
    setIsDarkWebSectionLoading(true);
    setTimeout(() => {
      setIsDarkWebSectionLoading(false);
      setShowDarkWebEmailSection(true);
    }, delayDarkWebLoading * 10);
  }

  useEffect(() => {
    setActiveClaiming(
      currentMonitors.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      // The format of queryResult from REMO has an extra data key.
      if (queryResult?.data?.entities) {
        setReport(ReportFactory.create(queryResult?.data, 'email'));
      } else {
        setReport(ReportFactory.create(queryResult?.data.data, 'email'));
      }
      setShowDarkWebEmailSection(false);
    }
  }, [queryResult.data, queryResult.isSuccess, reportOptions]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate) {
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    } else if (coreUpdateDate) {
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={10} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }

  const showReportClaiming =
    currentUserInfo?.email?.toLowerCase() ===
    report?.emailAddress?.toLowerCase();

  const MonitoringComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const { supportingEmail } = report;

  const { fraud_score, deliverability, recent_abuse, is_leaked } =
    supportingEmail || {};

  const queryParams = new URLSearchParams(search);
  const fallbackEmail = queryParams.get('email') || '';
  const lowerCaseEmailAddress =
    report?.emailAddress?.toLowerCase() ?? fallbackEmail;
  document.title = `${lowerCaseEmailAddress} - BeenVerified`;

  const handleOnBeginSearch = () => {
    trackEvent('email report promo', 'navigation click', 'contact search');
    redirectToSearchContactUrl({});
  };
  return (
    <Box margin={0} padding={0}>
      <Stack direction="row" position={'relative'}>
        <Box marginTop={0}>
          <EmailReportOverview
            email={lowerCaseEmailAddress}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({
                showHighConfidenceDataOnly: value,
              });
            }}
            personName={ownerName || lowerCaseEmailAddress}
            emailOverviewItemsData={{
              emails,
              jobs,
              educations,
              phones,
              profiles,
              usernames,
              peopleCount: report.data.people?.length,
            }}
            reportType={reportType}
            dateUpdated={reportUpdateDate}
          />

          {MonitoringComponent}

          <EmailFraudCheckSection
            fraudScore={fraud_score}
            deliverability={deliverability}
            abusiveBehavior={recent_abuse}
            leaked={is_leaked}
            showEmailFraudScan={supportingEmail != undefined}
            emailAddress={lowerCaseEmailAddress}
            showReportCTA
          />
          <PotentialOwnersV2
            potentialOwners={report.data.people || []}
            potentialOwnerIndex={reportOptions.potentialOwnerIndex}
            setPotentialOwnerIndex={(index: number) => {
              handleSetReportOptions({ potentialOwnerIndex: index });
            }}
            personName={ownerName || lowerCaseEmailAddress}
            permalink={permalink}
            reportType={reportType}
          />

          <PhoneSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            phoneList={phones}
            permalink={permalink}
          />
          <EmailSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            emailList={emails}
            permalink={permalink}
          />
          <AddressHistorySectionV2
            addresses={addresses}
            personName={ownerName || lowerCaseEmailAddress}
            permalink={permalink}
          />
          <SocialSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          <>
            {revealDarkWebEmailSection ? (
              darkWebBreaches?.length > 0 ? (
                <EmailDarkWebResults
                  email={lowerCaseEmailAddress ?? ownerName}
                  breaches={darkWebBreaches as SerializedDarkWebBreach[]}
                  lastUpdated={breachUpdateDate ?? ''}
                  permalink={permalink}
                />
              ) : (
                <EmailDarkWebNoBreaches
                  email={lowerCaseEmailAddress ?? ownerName}
                  lastUpdated={breachUpdateDate ?? ''}
                  permalink={permalink}
                />
              )
            ) : (
              <>
                {isDarkWebSectionLoading ? (
                  <EmailDarkWebLoadingScreen
                    email={lowerCaseEmailAddress}
                    delayDuration={delayDarkWebLoading}
                  />
                ) : (
                  isInEmailDarkWebVariation && (
                    <EmailDarkWebVerify
                      email={lowerCaseEmailAddress}
                      handleDarkWebLoadingScreen={
                        handleDarkWebSectionLoadingScreen
                      }
                    />
                  )
                )}
              </>
            )}
          </>
          <JobsSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            jobs={jobs}
            permalink={permalink}
          />
          <ContactReportSectionBanner
            image={imgIdCard}
            onBeginSearchClick={handleOnBeginSearch}
          />
          <EducationSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            educations={educations}
            permalink={permalink}
          />
          <AssetsSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />
          <NotesSectionV2 permalink={permalink} />
          <RelatedReportsSectionV2
            reportType="email"
            personName={ownerName || lowerCaseEmailAddress}
            potentialOwners={report?.data?.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRatingV2
            report_type={reportType}
            rating={report.data.meta?.rating || null}
          />
        </Box>
      </Stack>
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
}
